import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import classNames from 'classnames';
import { Check } from 'lucide-react';
import './Dropdown.css';

export function Dropdown({ 
  trigger,
  items = [],
  align = 'left',
  className = '',
  onOpen,
  isOpen: controlledIsOpen,
  onToggle,
  children,
  ...props
}) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [adjustedAlign, setAdjustedAlign] = useState(null);
  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        if (isControlled) {
          if (onToggle) {
            onToggle(false);
          }
        } else {
          setInternalIsOpen(false);
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isControlled, onToggle]);

  // Close on ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) {
        if (isControlled) {
          if (onToggle) {
            onToggle(false);
          }
        } else {
          setInternalIsOpen(false);
        }
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, isControlled, onToggle]);

  // Viewport boundary detection - adjust alignment if menu would overflow
  useLayoutEffect(() => {
    if (isOpen && menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const padding = 16; // Minimum distance from viewport edge

      // Check for right overflow
      if (rect.right > viewportWidth - padding) {
        setAdjustedAlign('right');
      } 
      // Check for left overflow
      else if (rect.left < padding) {
        setAdjustedAlign('left');
      } 
      // No overflow - use default alignment
      else {
        setAdjustedAlign(null);
      }
    } else if (!isOpen) {
      // Reset when closed
      setAdjustedAlign(null);
    }
  }, [isOpen]);

  const dropdownClasses = classNames(
    'agrisense-dropdown',
    className
  );

  // Use adjusted alignment if viewport detection determined overflow, otherwise use prop
  const effectiveAlign = adjustedAlign || align;

  const menuClasses = classNames(
    'agrisense-dropdown__menu',
    `agrisense-dropdown__menu--${effectiveAlign}`,
    {
      'agrisense-dropdown__menu--open': isOpen,
    }
  );

  const handleItemClick = (item) => {
    item.onClick();
    if (isControlled) {
      if (onToggle) {
        onToggle(false);
      }
    } else {
      setInternalIsOpen(false);
    }
  };

  const handleToggle = () => {
    const newIsOpen = !isOpen;
    if (isControlled) {
      // Controlled mode - call onToggle callback
      if (onToggle) {
        onToggle(newIsOpen);
      }
    } else {
      // Uncontrolled mode - manage internal state
      setInternalIsOpen(newIsOpen);
    }
    if (newIsOpen && onOpen) {
      onOpen();
    } else if (!newIsOpen) {
      // Dispatch close event when dropdown closes
      window.dispatchEvent(new CustomEvent('map:heatmap-dropdown-close'));
    }
  };

  // Filter out isOpen and onToggle from props to prevent them from being passed to DOM
  const { isOpen: _, onToggle: __, ...domProps } = props;
  
  return (
    <div className={dropdownClasses} ref={dropdownRef} {...domProps}>
      <div 
        className="agrisense-dropdown__trigger"
        onClick={handleToggle}
        data-testid="dropdown-trigger"
      >
        {trigger}
      </div>
      
      {isOpen && (
        <div className={menuClasses} data-testid="dropdown-menu" ref={menuRef}>
          {children || items.map((item, index) => (
            <React.Fragment key={index}>
              {item.divider ? (
                <div className="agrisense-dropdown__divider" />
              ) : (
                <button
                  className={classNames(
                    'agrisense-dropdown__item',
                    {
                      'agrisense-dropdown__item--active': item.active
                    }
                  )}
                  onClick={() => handleItemClick(item)}
                  data-testid="dropdown-item"
                >
                  {item.icon && (
                    <span className="agrisense-dropdown__item-icon">{item.icon}</span>
                  )}
                  <span>{item.label}</span>
                  {item.active && (
                    <span className="agrisense-dropdown__item-check">
                      <Check size={16} />
                    </span>
                  )}
                </button>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}